/**
 * Desk process session + away-time pause.
 *
 * Career time, auto-inbox trickle, and the Grok schedule brain run only while
 * this Windows desk process is open. No OS service, no Nanode RFQ job while
 * closed. A long real-time gap freezes the schedule — no proportional catch-up.
 * Inbox may still refill inside Settings caps (idleInbox.ts). That refill is
 * not a schedule catch-up and does not scale with the gap.
 */
import { wallElapsedMin } from "./autoInbox";
import {
  closeOrphanSessionsOnBoot,
  type FlightSession,
  type FlightSessionAuditEvent,
  type FlightSessionBook,
} from "./flightSessions";

/** Below this, a pause is a normal while-open interval, not an away gap. */
export const CLOSED_GAP_PAUSE_MIN = 30;

/**
 * Schedule / company catch-up stays 0. Inbox idle refill is a separate
 * bounded policy — never `floor(gap / interval)`, never a board write.
 */
export const CLOSED_GAP_MAX_CATCHUP_RFQS = 0;

/** One-line toast when the desk was closed this long. */
export const AWAY_PAUSE_TOAST_MIN = 3 * 24 * 60;

export const AWAY_PAUSE_TOAST = "Company paused while closed — board unchanged.";

export const DESK_SESSION_KIND = "northstar.dispatch.desk-session";

export type DeskSessionClock = {
  lastSessionClosedAtUtc: string | null;
  lastOpenedAtUtc: string | null;
};

export const EMPTY_DESK_SESSION: DeskSessionClock = {
  lastSessionClosedAtUtc: null,
  lastOpenedAtUtc: null,
};

export type ClosedGapReason = "no_gap" | "short_pause" | "paused";

export type ClosedGapDecision = {
  /** Wall minutes since last close, or since last trickle tick when close is unknown. */
  gapMin: number;
  isClosedGap: boolean;
  /** Always 0 — away time never catch-up files the schedule. */
  catchUpCount: number;
  /** Advance trickle / brain clocks to `now` so the stale gap is not “due”. */
  resetClocks: boolean;
  reason: ClosedGapReason;
  toast: string | null;
};

function isoOrNull(value: unknown): string | null {
  if (typeof value !== "string" || !value.trim()) return null;
  const t = new Date(value);
  if (Number.isNaN(t.getTime())) return null;
  return t.toISOString();
}

export function hydrateDeskSession(raw?: Partial<DeskSessionClock> | null): DeskSessionClock {
  return {
    lastSessionClosedAtUtc: isoOrNull(raw?.lastSessionClosedAtUtc),
    lastOpenedAtUtc: isoOrNull(raw?.lastOpenedAtUtc),
  };
}

export function markDeskSessionClosed(session: DeskSessionClock, now = new Date()): DeskSessionClock {
  return {
    ...hydrateDeskSession(session),
    lastSessionClosedAtUtc: now.toISOString(),
  };
}

export function markDeskSessionOpened(session: DeskSessionClock, now = new Date()): DeskSessionClock {
  return {
    ...hydrateDeskSession(session),
    lastOpenedAtUtc: now.toISOString(),
  };
}

export function closedGapMinutes(input: {
  lastSessionClosedAtUtc?: string | null;
  lastTickAtUtc?: string | null;
  lastPassAtUtc?: string | null;
  now?: Date;
}): number {
  const now = input.now ?? new Date();
  if (input.lastSessionClosedAtUtc) {
    const closed = wallElapsedMin(input.lastSessionClosedAtUtc, now);
    return Number.isFinite(closed) && closed < Number.POSITIVE_INFINITY ? Math.max(0, closed) : 0;
  }
  const tick = wallElapsedMin(input.lastTickAtUtc, now);
  const pass = wallElapsedMin(input.lastPassAtUtc, now);
  const candidates = [tick, pass].filter((n) => Number.isFinite(n) && n < Number.POSITIVE_INFINITY);
  if (!candidates.length) return 0;
  return Math.max(0, Math.max(...candidates));
}

/**
 * Schedule catch-up count. Locked to 0 — the board does not fast-forward.
 * Slider rate and Inbox idle caps apply only while the desk is open.
 */
export function catchUpRfqsForClosedGap(_input?: {
  gapMin?: number;
  pendingCount?: number;
  enabled?: boolean;
}): number {
  return CLOSED_GAP_MAX_CATCHUP_RFQS;
}

/** The formula we must never use — tests prove we do not mint `floor(gap / interval)`. */
export function unboundedCatchUpRfqs(gapMin: number, intervalMin: number): number {
  const interval = intervalMin > 0 ? intervalMin : 480;
  return Math.max(0, Math.floor(Math.max(0, gapMin) / interval));
}

/**
 * Shared away-time policy for Classic auto-inbox and the Grok brain.
 * Long gap → reset clocks, mint zero RFQs, optional toast after ~3 days.
 */
export function decideClosedSessionGap(input: {
  lastSessionClosedAtUtc?: string | null;
  lastOpenedAtUtc?: string | null;
  lastTickAtUtc?: string | null;
  lastPassAtUtc?: string | null;
  pendingCount?: number;
  enabled?: boolean;
  now?: Date;
}): ClosedGapDecision {
  const now = input.now ?? new Date();
  const opened = input.lastOpenedAtUtc ? new Date(input.lastOpenedAtUtc).getTime() : NaN;
  const closed = input.lastSessionClosedAtUtc ? new Date(input.lastSessionClosedAtUtc).getTime() : NaN;
  if (Number.isFinite(opened) && Number.isFinite(closed) && opened >= closed) {
    return {
      gapMin: 0,
      isClosedGap: false,
      catchUpCount: 0,
      resetClocks: false,
      reason: "no_gap",
      toast: null,
    };
  }
  const gapMin = closedGapMinutes({
    lastSessionClosedAtUtc: input.lastSessionClosedAtUtc,
    lastTickAtUtc: input.lastTickAtUtc,
    lastPassAtUtc: input.lastPassAtUtc,
    now,
  });
  if (gapMin < CLOSED_GAP_PAUSE_MIN) {
    return {
      gapMin,
      isClosedGap: false,
      catchUpCount: 0,
      resetClocks: false,
      reason: gapMin <= 0 ? "no_gap" : "short_pause",
      toast: null,
    };
  }
  return {
    gapMin,
    isClosedGap: true,
    catchUpCount: 0,
    resetClocks: true,
    reason: "paused",
    toast: gapMin >= AWAY_PAUSE_TOAST_MIN ? AWAY_PAUSE_TOAST : null,
  };
}

/**
 * Desk open. Closes flight sessions this tail still has open after 6 hours
 * with no fresh telemetry. The away-time clock is unchanged.
 */
export function bootDeskFlightSessions(
  book: FlightSessionBook,
  input?: { now?: Date; lastTelemetryAt?: string | null },
): { book: FlightSessionBook; closedIds: string[] } {
  return closeOrphanSessionsOnBoot(book, {
    now: input?.now,
    lastTelemetryAt: input?.lastTelemetryAt,
  });
}

export function bootDeskClockAndSessions(input: {
  clock: DeskSessionClock;
  sessions: FlightSession[];
  audit: FlightSessionAuditEvent[];
  now?: Date;
  lastTelemetryAt?: string | null;
}): { clock: DeskSessionClock; sessions: FlightSession[]; audit: FlightSessionAuditEvent[]; closedIds: string[] } {
  const now = input.now ?? new Date();
  const booted = bootDeskFlightSessions(
    { sessions: input.sessions, audit: input.audit },
    { now, lastTelemetryAt: input.lastTelemetryAt },
  );
  return {
    clock: markDeskSessionOpened(input.clock, now),
    sessions: booted.book.sessions,
    audit: booted.book.audit,
    closedIds: booted.closedIds,
  };
}

export function closedGapCaption(decision: ClosedGapDecision): string {
  if (!decision.isClosedGap) {
    return "Away: pause company (recommended). Schedule stays frozen. Idle Inbox refill, when on, may add a few capped RFQs after reopen — not a closed-gap pile.";
  }
  return AWAY_PAUSE_TOAST;
}
